import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { VerificationBadge } from "@/pages/AskTheCandidates";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ShieldAlert, Lock, CalendarDays, CheckCircle2, Info, Vote } from "lucide-react";

/** Phase 10.5 — Candidate Portal (private token link; preview-phase access).
 * Scope: own Q&A + public aggregate info ONLY. Portal access never implies verification. */

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
  } catch { return iso; }
};

const QuestionBlock = ({ token, q, onSubmitted }) => {
  const [text, setText] = useState(q.my_answer || "");
  const [sourceUrl, setSourceUrl] = useState(q.my_source_url || "");
  const [saving, setSaving] = useState(false);
  const over = text.length > q.char_limit;

  const submit = async () => {
    if (!text.trim()) { toast.error("Please write an answer first."); return; }
    if (over) { toast.error(`Your answer exceeds the ${q.char_limit}-character limit.`); return; }
    setSaving(true);
    try {
      const r = await api.post(`/public/candidate-portal/${token}/answers`, {
        question_id: q.question_id, text: text.trim(), source_url: sourceUrl.trim() || null,
      });
      toast.success(r.data.note || "Answer submitted.");
      onSubmitted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit your answer.");
    } finally { setSaving(false); }
  };

  return (
    <Card data-testid={`portal-question-${q.question_id}`}>
      <CardContent className="p-5 sm:p-6">
        <p className="font-heading font-semibold text-foreground" data-testid="portal-question-text">{q.question}</p>
        <div className="flex flex-wrap gap-2 mt-2">
          <Badge variant="outline" className="bg-secondary text-foreground border-slate-200 text-[10px] gap-1">
            <CalendarDays className="h-3 w-3" /> Deadline: {fmtDate(q.deadline)}
          </Badge>
          <Badge variant="outline" className="bg-secondary text-foreground border-slate-200 text-[10px]">
            {q.char_limit}-character limit — identical for every candidate
          </Badge>
          {q.my_answer && (
            <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-300 text-[10px] gap-1" data-testid="portal-answered-badge">
              <CheckCircle2 className="h-3 w-3" /> Answered {fmtDate(q.submitted_at)}
            </Badge>
          )}
          {q.updated_at && (
            <Badge variant="outline" className="bg-sky-50 text-sky-800 border-sky-200 text-[10px]">
              Updated {fmtDate(q.updated_at)}
            </Badge>
          )}
        </div>

        {q.can_answer ? (
          <div className="mt-4">
            <Label className="text-xs text-muted-foreground">Your answer (shown publicly exactly as submitted)</Label>
            <Textarea className="mt-1 min-h-40" value={text} maxLength={q.char_limit + 500}
                      onChange={(e) => setText(e.target.value)} data-testid="portal-answer-textarea" />
            <p className={`text-[11px] mt-1 ${over ? "text-destructive font-semibold" : "text-muted-foreground"}`}
               data-testid="portal-char-count">
              {text.length} / {q.char_limit} characters{over ? " — over the limit" : ""}
            </p>
            <div className="mt-3 w-full sm:w-96">
              <Label className="text-xs text-muted-foreground">Optional source / supporting link</Label>
              <Input className="mt-1" placeholder="https://…" value={sourceUrl} maxLength={800}
                     onChange={(e) => setSourceUrl(e.target.value)} data-testid="portal-source-input" />
            </div>
            <Button className="mt-4 bg-primary hover:bg-primary/90" onClick={submit} disabled={saving}
                    data-testid="portal-submit-answer-btn">
              {saving ? "Submitting…" : q.my_answer ? "Update my answer" : "Submit my answer"}
            </Button>
            {q.my_answer && (
              <p className="text-[11px] text-muted-foreground mt-2">
                Updating your answer will publicly show an “Updated” date. The previous version is preserved internally.
              </p>
            )}
          </div>
        ) : (
          <div className="mt-4 rounded-lg border border-slate-200 bg-secondary p-4">
            <p className="text-sm text-foreground inline-flex items-center gap-2">
              <Lock className="h-4 w-4 text-muted-foreground" />
              {q.my_answer ? "This question is closed. Your submitted answer remains publicly visible."
                : "This question is closed and can no longer be answered."}
            </p>
            {q.my_answer && <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{q.my_answer}</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const TransparencyBlock = ({ token, t, onSubmitted }) => {
  const item = t.item;
  const [choice, setChoice] = useState(item.choice || "");
  const [explanation, setExplanation] = useState(item.explanation || "");
  const [saving, setSaving] = useState(false);
  const limit = t.limit;
  const over = explanation.length > limit;
  const answered = item.status === "answered";

  const submit = async () => {
    if (!choice) { toast.error("Please choose Yes, No or Other."); return; }
    if (over) { toast.error(`Explanation exceeds the ${limit}-character limit.`); return; }
    setSaving(true);
    try {
      const r = await api.post(`/public/candidate-portal/${token}/transparency`, {
        question_key: item.key, choice, explanation: explanation.trim() || null,
      });
      toast.success(r.data.note || "Answer submitted.");
      onSubmitted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit your answer.");
    } finally { setSaving(false); }
  };

  return (
    <div className="border-b border-slate-100 last:border-0 py-4" data-testid={`portal-transparency-${item.key}`}>
      <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{item.title}</p>
      <p className="text-sm font-semibold text-foreground mt-0.5">{item.question}</p>
      <RadioGroup value={choice} onValueChange={setChoice} className="flex flex-wrap gap-4 mt-3">
        {["yes", "no", "other"].map((c) => (
          <label key={c} className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
            <RadioGroupItem value={c} data-testid={`portal-transparency-${item.key}-${c}`} />
            {c === "yes" ? "Yes" : c === "no" ? "No" : "Other"}
          </label>
        ))}
      </RadioGroup>
      <div className="mt-2">
        <Label className="text-xs text-muted-foreground">Explain your answer (optional, up to {limit} characters)</Label>
        <Textarea className="mt-1 min-h-20" value={explanation} maxLength={limit + 100}
                  onChange={(e) => setExplanation(e.target.value)}
                  data-testid={`portal-transparency-${item.key}-explanation`} />
        <p className={`text-[11px] mt-1 ${over ? "text-destructive font-semibold" : "text-muted-foreground"}`}>
          {explanation.length} / {limit}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <Button size="sm" className="bg-primary hover:bg-primary/90" onClick={submit} disabled={saving}
                data-testid={`portal-transparency-${item.key}-submit`}>
          {saving ? "Submitting…" : answered ? "Update answer" : "Submit answer"}
        </Button>
        {answered && (
          <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-300 text-[10px] gap-1">
            <CheckCircle2 className="h-3 w-3" /> Answered
          </Badge>
        )}
        {item.updated_at && (
          <Badge variant="outline" className="bg-sky-50 text-sky-800 border-sky-200 text-[10px]">
            Updated {fmtDate(item.updated_at)}
          </Badge>
        )}
      </div>
    </div>
  );
};

export default function CandidatePortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api.get(`/public/candidate-portal/${token}`)
      .then((r) => { setData(r.data); setError(null); })
      .catch((e) => setError(e?.response?.data?.detail || "This portal link is not valid."))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="wyv-container py-14 max-w-3xl"><Skeleton className="h-96" /></div>;

  if (error) {
    return (
      <div className="wyv-container py-14 max-w-2xl">
        <Card data-testid="portal-error">
          <CardContent className="p-8 text-center">
            <ShieldAlert className="h-9 w-9 text-amber-600 mx-auto mb-2" />
            <h1 className="font-heading font-bold text-xl text-foreground">Portal link problem</h1>
            <p className="text-sm text-muted-foreground mt-2" data-testid="portal-error-detail">{error}</p>
            <Button asChild size="sm" variant="outline" className="mt-5">
              <Link to="/contact">Contact WitnessYourVote</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const c = data.candidate;
  return (
    <div className="wyv-container py-10 sm:py-14 max-w-3xl">
      <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200 mb-3">Candidate Portal — Private</Badge>
      <h1 className="font-heading font-bold text-3xl text-foreground" data-testid="portal-candidate-name">{c.name}</h1>
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <VerificationBadge status={c.verification_status} />
        <span className="text-[11px] text-muted-foreground">
          Verification status is managed separately by WitnessYourVote from authoritative election sources —
          portal access does not change it.
        </span>
      </div>

      <div className="mt-4 rounded-lg border border-slate-200 bg-secondary p-3.5" data-testid="portal-scope-note">
        <p className="text-xs text-muted-foreground inline-flex items-start gap-2">
          <Info className="h-4 w-4 shrink-0 mt-0.5 text-accent" /> {data.scope_note}
        </p>
      </div>

      <h2 className="font-heading font-bold text-lg text-foreground mt-8 mb-3">Current community questions</h2>
      {data.current_questions.length === 0 ? (
        <Card data-testid="portal-no-current">
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">No open questions right now. You will be notified when a new community question opens.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {data.current_questions.map((q) => (
            <QuestionBlock key={q.question_id} token={token} q={q} onSubmitted={load} />
          ))}
        </div>
      )}

      {data.previous_questions.length > 0 && (
        <div className="mt-8">
          <h2 className="font-heading font-bold text-lg text-foreground mb-3">Previous questions</h2>
          <div className="space-y-4">
            {data.previous_questions.map((q) => (
              <QuestionBlock key={q.question_id} token={token} q={q} onSubmitted={load} />
            ))}
          </div>
        </div>
      )}

      {data.transparency && (
        <div className="mt-8" data-testid="portal-transparency-section">
          <h2 className="font-heading font-bold text-lg text-foreground inline-flex items-center gap-2">
            <Vote className="h-5 w-5 text-accent" /> Election Transparency Questions
          </h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-xl">{data.transparency.note}</p>
          <Card className="mt-3">
            <CardContent className="p-5 sm:p-6">
              {data.transparency.items.map((item) => (
                <TransparencyBlock key={item.key} token={token}
                                   t={{ item, limit: data.transparency.explanation_limit }}
                                   onSubmitted={load} />
              ))}
              <p className="text-[11px] text-muted-foreground italic mt-3" data-testid="portal-transparency-disclaimer">
                {data.transparency.disclaimer}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground italic mt-8">
        This private link is for you only. If you believe it has been shared or compromised,
        contact WitnessYourVote and a replacement link will be issued.
        See your public Q&A record on <Link to="/ask-the-candidates" className="text-primary underline underline-offset-2">Ask the Candidates</Link>.
      </p>
    </div>
  );
}
